import React, { useState, useEffect } from 'react';
import { db, functions } from '../../firebase.js';
import { httpsCallable } from 'firebase/functions';
import { collection, query, where, onSnapshot, orderBy, getDocs } from 'firebase/firestore';
import Swal from 'sweetalert2';
import '../../styles/GestionDePagos.css'; // Reutilizamos algunos estilos
import '../../styles/GestionAcelerador.css'; // Estilos específicos para las pestañas
import Pagination from '../../components/common/Pagination.js'; // Importar paginación

// Helper to create grouped callable functions
const createApiCallable = (functionName) => {
    const callable = httpsCallable(functions, 'api');
    return (data) => {
        const requestData = { ...data, __fn: functionName };
        return callable(requestData);
    };
};

const approveRequest = createApiCallable('approveAcceleratorRequest');
const rejectRequest = createApiCallable('rejectAcceleratorRequest');

const GestionAcelerador = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [processingId, setProcessingId] = useState(null);
    const [activeTab, setActiveTab] = useState('pendiente');
    const [currentPage, setCurrentPage] = useState(1); // Estado para la página actual
    const [itemsPerPage, setItemsPerPage] = useState(15);

    useEffect(() => {
        setLoading(true);
        setError(null);
        setRequests([]);
        setCurrentPage(1); // Resetear a la primera página al cambiar de pestaña

        const collectionsToWatch = [
            { name: "accelerator_requests", type: 'balance' },
            { name: "accelerator_reactivation_requests", type: 'manual' }
        ];

        const queries = collectionsToWatch.map(c => query(
            collection(db, c.name),
            where("status", "==", activeTab),
            orderBy("createdAt", "desc")
        ));

        // 1. Fetch initial data quickly for a better user experience.
        const fetchInitialData = async () => {
            try {
                const querySnapshots = await Promise.all(queries.map(q => getDocs(q)));
                const allRequests = querySnapshots.flatMap((snapshot, index) => {
                    const { type } = collectionsToWatch[index];
                    return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id, type }));
                });
                setRequests(allRequests);
            } catch (err) {
                console.error("Error fetching initial accelerator requests:", err);
                setError('Error al cargar las solicitudes.');
            } finally {
                setLoading(false); // Turn off loading only after initial fetch is complete.
            }
        };

        fetchInitialData();

        // 2. Attach real-time listeners to keep data in sync.
        const unsubscribes = queries.map((q, index) => {
            const { type } = collectionsToWatch[index];
            return onSnapshot(q, (snapshot) => {
                const newRequests = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id, type }));
                setRequests(prevRequests => {
                    // Filter out old requests of the same type and merge with new ones
                    const otherRequests = prevRequests.filter(r => r.type !== type);
                    return [...otherRequests, ...newRequests];
                });
            }, (err) => {
                console.error(`Error listening to ${collectionsToWatch[index].name}:`, err);
                // Optionally set an error for real-time updates, but don't overwrite initial data
            });
        });

        return () => {
            // Unsubscribe from all listeners on cleanup.
            unsubscribes.forEach(unsub => unsub());
        };
    }, [activeTab]);

    const handleApprove = async (req) => {
        setProcessingId(req.id);
        try {
            await approveRequest({ requestId: req.id, requestType: req.type, agreementId: req.agreementId });
            Swal.fire('Aprobado', 'La solicitud ha sido aprobada exitosamente.', 'success');
        } catch (error) {
            console.error("Error approving request:", error);
            Swal.fire('Error', `Hubo un problema al aprobar: ${error.message}`, 'error');
        } finally {
            setProcessingId(null);
        }
    };

    const handleReject = async (req) => {
        const { value: reason } = await Swal.fire({
            title: 'Rechazar Solicitud',
            input: 'text',
            inputLabel: 'Motivo del rechazo',
            inputPlaceholder: 'Escribe el motivo aquí...',
            showCancelButton: true,
            confirmButtonText: 'Confirmar Rechazo',
            cancelButtonText: 'Cancelar',
            inputValidator: (value) => {
                if (!value) {
                    return '¡Necesitas escribir un motivo!'
                }
            }
        });

        if (reason) {
            setProcessingId(req.id);
            try {
                await rejectRequest({ requestId: req.id, requestType: req.type, reason });
                Swal.fire('Rechazado', 'La solicitud ha sido rechazada.', 'success');
            } catch (error) {
                console.error("Error rejecting request:", error);
                Swal.fire('Error', `Hubo un problema al rechazar: ${error.message}`, 'error');
            } finally {
                setProcessingId(null);
            }
        }
    };

    const totalPages = Math.ceil(requests.length / itemsPerPage);

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const renderContent = () => {
        if (loading) {
            return <p>Cargando solicitudes...</p>;
        }
        if (error) {
            return <p className="error-message">{error}</p>;
        }
        if (requests.length === 0) {
            return <p className="empty-state-text">No hay solicitudes en estado '{activeTab}'.</p>;
        }

        // Lógica de paginación
        const sortedRequests = requests.sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis());
        const paginatedRequests = sortedRequests.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

        return (
            <>
                <div className="solicitudes-grid-compact">
                    {paginatedRequests.map(req => (
                        <div key={req.id} className="solicitud-card-compact">
                            <div className="solicitud-card-compact-header">
                                <span className="user-email" title={req.userEmail}>{req.userEmail}</span>
                                <span className="date">{req.createdAt?.toDate().toLocaleString('es-ES') || 'N/A'}</span>
                            </div>
                            <div className="solicitud-card-compact-body">
                                <div className="info-row">
                                    <span className="label">Acuerdo ID:</span>
                                    <span className="value">{req.agreementId?.substring(0, 6) || 'N/A'}...</span>
                                </div>
                                <div className="info-row">
                                    <span className="label">Comisión:</span>
                                    <span className="value amount">${(req.feePaid || req.feeAmount || 0).toFixed(2)}</span>
                                </div>
                                <div className="info-row">
                                    <span className="label">Tipo:</span>
                                    <span className="value">{req.type === 'balance' ? 'Pagado con Saldo' : 'Solicitud Manual'}</span>
                                </div>
                                {activeTab === 'rechazado' && req.rejectionReason && (
                                    <div className="info-row rejection-reason">
                                        <span className="label">Motivo Rechazo:</span>
                                        <span className="value">{req.rejectionReason}</span>
                                    </div>
                                )}
                            </div>
                            {activeTab === 'pendiente' && (
                                <div className="solicitud-card-compact-actions">
                                    <button onClick={() => handleApprove(req)} disabled={processingId === req.id} className="approve-btn">
                                        {processingId === req.id ? '...' : 'Aprobar'}
                                    </button>
                                    <button onClick={() => handleReject(req)} disabled={processingId === req.id} className="reject-btn">
                                        {processingId === req.id ? '...' : 'Rechazar'}
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                {totalPages > 1 && (
                    <Pagination
                        totalItems={requests.length}
                        itemsPerPage={itemsPerPage}
                        onItemsPerPageChange={setItemsPerPage}
                        currentPage={currentPage}
                        onNext={handleNextPage}
                        onPrevious={handlePreviousPage}
                        canNext={currentPage < totalPages}
                        canPrevious={currentPage > 1}
                    />
                )}
            </>
        );
    };

    return (
        <div className="main-panel">
            <h2 className="section-title">Solicitudes de Acelerador</h2>
            <p>Gestiona las solicitudes de reactivación de aceleradores, tanto las pagadas con balance como las manuales.</p>
            
            <div className="tabs-container">
                <button className={`tab-button ${activeTab === 'pendiente' ? 'active' : ''}`} onClick={() => setActiveTab('pendiente')}>
                    Pendientes
                </button>
                <button className={`tab-button ${activeTab === 'aprobado' ? 'active' : ''}`} onClick={() => setActiveTab('aprobado')}>
                    Aprobadas
                </button>
                <button className={`tab-button ${activeTab === 'rechazado' ? 'active' : ''}`} onClick={() => setActiveTab('rechazado')}>
                    Rechazadas
                </button>
            </div>
            
            {renderContent()}
        </div>
    );
};

export default GestionAcelerador;